import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = logged out, object = logged in
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setUser(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            console.warn("[auth] /auth/me failed, clearing token:", e);
            localStorage.removeItem(TOKEN_KEY);
            setUser(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return {
                ok: false,
                error: formatApiError(e.response?.data?.detail) || e.message,
            };
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, login, logout, refresh }),
        [user, login, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
